import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withDevtools } from '@ngrx-toolkit/core';
import { pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { addHours, addMinutes, getUnixTime } from 'date-fns';
import { ApiService } from 'src/api';
import { AnnotationType, SearchResponse } from 'src/api/models';
import { trackEvent } from 'src/app/core/services/google-analytics';
import { GoogleAnalyticsEvent } from 'src/app/shared/enums/google-analytics-event.enum';
import { RoutePath } from 'src/app/shared/enums/route-path.enum';
import { SearchType } from 'src/app/shared/enums/search-type.enum';
import { ErrorModel } from 'src/app/shared/models/app.state';
import { FILTER_FORM_CONTROLS } from 'src/app/shared/models/filter-form-controls.enum';
import { FilterSearchFormModel } from 'src/app/shared/models/filter-search-form.model';
import { FilterSearchModelType } from 'src/app/shared/models/filter-search-model-type.enum';
import { FilterSearchRequestModel } from 'src/app/shared/models/filter-search-request.model';
import { FilterSearchResultModel } from 'src/app/shared/models/filter-search-result.model';
import { FilterSearchTagModel } from 'src/app/shared/models/filter-search-tag.model';
import {
  ALMOST_ONE_DAY_HOURS,
  ALMOST_ONE_HOUR_MINUTES,
  TEMPLATE_NULL,
} from 'src/app/features/search/search.constants';
import { catchApiError } from './store.operators';

export const initialSearchFilters: FilterSearchFormModel = {
  text: null,
  type: SearchType.BOTH,
  typeId: null,
  from: null,
  to: null,
};

interface FilterSearchState {
  loaded: boolean;
  loading: boolean;
  errors: ErrorModel[];
  entity: SearchResponse;
  metadata: {
    tags: AnnotationType[];
    filterOptions: FilterSearchFormModel;
  };
}

const initialState: FilterSearchState = {
  loaded: false,
  loading: false,
  errors: [],
  entity: {
    annotations: [],
    recordings: [],
  },
  metadata: {
    tags: [],
    filterOptions: initialSearchFilters,
  },
};

export const sortResponse = (
  a: FilterSearchResultModel,
  b: FilterSearchResultModel,
): number => {
  const getTime = (item: FilterSearchResultModel) =>
    item.itemType === FilterSearchModelType.ANNOTATION
      ? item.timeStamp
      : item.dateTime;

  return (getTime(a) - getTime(b)) * -1;
};

const toFilterSearchRequest = (
  options: FilterSearchFormModel,
): FilterSearchRequestModel =>
  Object.entries(options)
    .filter(([, value]) => !!value && value !== TEMPLATE_NULL)
    .reduce(
      (acc, [key, value]) =>
        key === FILTER_FORM_CONTROLS.FROM
          ? {
              ...acc,
              from: getUnixTime(value as Date),
            }
          : key === FILTER_FORM_CONTROLS.TO
            ? {
                ...acc,
                to: getUnixTime(
                  addMinutes(
                    addHours(value as Date, ALMOST_ONE_DAY_HOURS),
                    ALMOST_ONE_HOUR_MINUTES,
                  ),
                ),
              }
            : {
                ...acc,
                [key]: value,
              },
      {},
    );

/**
 * Whether the form narrows anything at all. `BOTH`/`TEMPLATE_NULL` are the
 * form's "everything" values, so they do not count as a query and an
 * untouched form must not hit the API.
 */
const narrowsAnything = (options: FilterSearchFormModel): boolean =>
  Object.keys(toFilterSearchRequest(options)).some(key => key !== 'type') ||
  (!!options.type && options.type !== SearchType.BOTH);

export const FilterSearchStore = signalStore(
  withDevtools('filterSearch'),
  withState(() => initialState),
  withProps(() => ({
    _router: inject(Router),
  })),
  withComputed(store => ({
    filterSearchOptions: computed(() => store.metadata.filterOptions()),
    filterSearchTags: computed((): FilterSearchTagModel[] => {
      const tagList = store.metadata
        .tags()
        .map(({ name, id }) => ({ name, id }));
      return tagList.filter(
        (tag, index, self) => index === self.findIndex(t => t.id === tag.id),
      );
    }),
    filterSearchResult: computed((): FilterSearchResultModel[] => {
      const { annotations, recordings } = store.entity();
      return [
        ...annotations!.map((a): FilterSearchResultModel => ({
          ...a,
          itemType: FilterSearchModelType.ANNOTATION,
        })),
        ...recordings!.map((m): FilterSearchResultModel => ({
          ...m,
          itemType: FilterSearchModelType.RECORDING,
        })),
      ].sort((a, b) => sortResponse(a, b));
    }),
    /* Nothing typed and no chip picked: the page is idle, not empty-result. */
    filterSearchIdle: computed(
      () => !narrowsAnything(store.metadata.filterOptions()),
    ),
    filterSearchNoResult: computed(
      () =>
        store.loaded() &&
        !store.loading() &&
        store.entity().annotations!.length === 0 &&
        store.entity().recordings!.length === 0,
    ),
  })),
  withMethods(store => {
    const apiClient = inject(ApiService);

    return {
      getFilterSearch: rxMethod<FilterSearchRequestModel>(
        pipe(
          tap(options => {
            trackEvent(GoogleAnalyticsEvent.FILTER, {
              type: options.type,
              byText: !!options.text,
              useFrom: !!options.from,
              useTo: !!options.to,
              byTypeId: !!options.typeId,
            });
            patchState(store, { loading: true });
          }),
          switchMap(options =>
            apiClient.searchGet({ ...options }).pipe(
              tap(response => {
                patchState(store, {
                  entity: {
                    annotations: response.annotations || [],
                    recordings: response.recordings || [],
                  },
                  loading: false,
                  loaded: true,
                });
              }),
              catchApiError(error => {
                patchState(store, {
                  errors: [error as ErrorModel],
                  loaded: true,
                  loading: false,
                });
              }),
            ),
          ),
        ),
      ),
      setFilterSearchOptions(options: Partial<FilterSearchFormModel>) {
        patchState(store, state => ({
          metadata: {
            ...state.metadata,
            filterOptions: {
              ...state.metadata.filterOptions,
              ...options,
            },
          },
        }));

        const filterOptions = store.metadata.filterOptions();

        if (!narrowsAnything(filterOptions)) {
          // Clearing the last filter puts the page back to its idle state
          // rather than asking the API for everything.
          patchState(store, {
            loaded: false,
            loading: false,
            entity: { annotations: [], recordings: [] },
          });
          return;
        }

        this.getFilterSearch(toFilterSearchRequest(filterOptions));
      },
      getFilterSearchTags: rxMethod<void>(
        pipe(
          switchMap(() =>
            apiClient.annotationTypesGet({ shared: true }).pipe(
              tap(tags =>
                patchState(store, state => ({
                  metadata: { ...state.metadata, tags },
                })),
              ),
              catchApiError(error => {
                patchState(store, {
                  errors: [error as ErrorModel],
                  loaded: true,
                  loading: false,
                });
              }),
            ),
          ),
        ),
      ),
      resetSearching() {
        patchState(store, state => ({
          loaded: false,
          entity: {
            annotations: [],
            recordings: [],
          },
          metadata: {
            ...state.metadata,
            filterOptions: initialSearchFilters,
          },
        }));

        store._router.navigate([RoutePath.Platform, RoutePath.Recordings]);
      },
    };
  }),
);
